// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from 'react-native';

import {
  HoleMapCanvas,
  type KnownHoleMarker,
  type MapOverlayPolygon,
} from '@/features/hole/holeview/HoleMapCanvas';
import {
  DRAWING_FILLS,
  OVERLAY_COLORS,
  ringAreaSquareMeters,
} from '@/features/hole/holeview/holeShapes';
import { parseLatLon } from './coordinateInput';
import { correctedCoursePosition } from './coursePositionFromGeometry';
import { requestCourseAnalysis, useCourseAnalysisStatus } from './useCourseAnalysisStatus';
import {
  IDENTITY_VIEW,
  MAX_VIEW_ZOOM,
  MIN_EDIT_ZOOM,
  MIN_VIEW_ZOOM,
  pointInPolygon,
  type LatLon,
  type ViewTransform,
} from '@/features/hole/holeview/tileMath';
import type { TeePoint } from '@/features/hole/holeview/useHoleViewGeometry';
import { formatArea, formatHoleDistance } from '@/features/units/distance';
import { useI18n } from '@/i18n';
import { useDistanceUnit } from '@/stores/distanceUnitState';
import appColors from '@/theme/colors';

import {
  draftFromFeatures,
  isDirty as isDraftDirty,
  markerKey,
  placeMarker,
  removeMarker,
  removedFeatureIds,
  toManualFeatures,
  type HoleDraft,
} from './holeGeometryDraft';
import {
  framingRingsForHole,
  layersOf,
  shapesForHole,
  toggleKind,
  visibleShapes,
} from './holeShapeLayers';
import { useHoleFeatures, type HoleFeature, type HoleShapeFeature } from './useHoleFeatures';
import { useManualHoleFeatures } from './useManualHoleFeatures';

/** "satellite" alone is ambiguous now that two imagery providers both write rows. */
export const sourceLabel = (source: string, sourceProvider: string | null) =>
  source === 'satellite' && sourceProvider ? `${source} · ${sourceProvider}` : source;

/**
 * Which of `shapes` a tapped point lands on, topmost first — the last drawn, same convention
 * `HoleGuideCard` uses for its own shape-tap hit test.
 *
 * Ray-cast in plain lat/lon rather than a projected space: the ring and the tapped point come
 * from the same `onMapPress` un-projection either way, and a hole's ground footprint is far too
 * small for that to distort the topology `pointInPolygon` tests.
 */
export const shapeAt = (shapes: HoleShapeFeature[], point: LatLon): HoleShapeFeature | null => {
  for (let index = shapes.length - 1; index >= 0; index -= 1) {
    const shape = shapes[index];
    const ring = shape.ring.map((vertex) => ({ x: vertex.lon, y: vertex.lat }));
    if (pointInPolygon({ x: point.lon, y: point.lat }, ring)) return shape;
  }
  return null;
};

export type CourseHoleMapModalProps = {
  visible: boolean;
  onClose: () => void;
  apiBaseUrl: string;
  token: string | null | undefined;
  courseId: string | null;
  /**
   * Every hole the course has, so holes with no geometry can still be paged to. Without it
   * only mapped holes are reachable, which excludes exactly the holes worth editing.
   */
  holeNumbers?: number[];
  /** The club's own position, used to frame a hole that has nothing marked yet. */
  center?: LatLon | null;
  /** Whether marking positions is offered. Admin and web only; see the caller. */
  editable?: boolean;
  /**
   * The hole to open on, when the caller knows one — from a URL, say. Seeded once when the
   * modal opens; after that, paging inside owns which hole is shown, so re-supplying this
   * later (the caller's own URL syncing to `onHoleChange` echoing back in, for instance)
   * cannot fight the admin's own navigation.
   */
  initialHole?: number | null;
  /**
   * Fires whenever the displayed hole changes, while the modal is open. For a caller that
   * wants to keep something else — a URL — in sync with where the admin has paged to.
   */
  onHoleChange?: (holeNumber: number | null) => void;
};

/**
 * The picture drawn under the shapes — purely the background, independent of which shapes
 * are shown. Matching `IMAGERY_PROVIDER_NAME`/`IMAGERY_STAC_PROVIDER_NAME` in
 * course-analysis-service's `providers.py`. "Drawing" is not a provider; it shows no picture
 * at all, with the flat cartographic fills carrying the shapes instead.
 */
type Basemap = 'esri-worldimagery' | 'lantmateriet-orto' | 'drawing';
const BASEMAPS: Basemap[] = ['esri-worldimagery', 'lantmateriet-orto', 'drawing'];

/**
 * Which shape sources are drawn, independent of the basemap picture. Unlike the basemap this
 * is a set, not a single pick — combining a provider's satellite detections with OSM's own
 * import is a legitimate way to look at a hole, not just a step on the way to picking one.
 */
type SourceOption = 'esri-worldimagery' | 'lantmateriet-orto' | 'osm';
const SOURCE_OPTIONS: SourceOption[] = ['esri-worldimagery', 'lantmateriet-orto', 'osm'];

/** How much ground to show around the club when a hole has no geometry at all. */
const FALLBACK_SPAN_METERS = 500;

/**
 * A stable empty tee list for the course-overview page. That page draws no single active hole —
 * it frames on every hole at once (see `overviewFrame`) and lets the numbered markers carry it —
 * so a fresh `[]` here every render would needlessly invalidate the canvas's fit memo.
 */
const NO_TEES: TeePoint[] = [];

/**
 * Tighter than {@link FALLBACK_SPAN_METERS}: a predicted centre is a specific neighbouring
 * hole rather than "somewhere on the property", so there is no need to zoom out as far to
 * find it.
 */
const PREDICTED_SPAN_METERS = 300;

/**
 * Tighter than the map canvas's own default framing margins (a quarter of the width, a tenth
 * of the length) — roughly half of each. Applied only once a hole has a confirmed tee and
 * green; see `holeIsMapped`.
 */
const MAPPED_HORIZONTAL_PAD = 0.12;
const MAPPED_VERTICAL_PAD = 0.05;

const ZOOM_STEP = 1.6;

/**
 * Hole-by-hole view of a course's mapped geometry, for administrators.
 *
 * The in-round hole view answers "where am I hitting"; this answers "is this course mapped
 * correctly". Hence the differences: it pages through every hole rather than showing the one
 * being played, and it lists the actual coordinates and their provenance beside the picture,
 * because a wrong position is far easier to spot as a number next to a map than as a marker
 * in the wrong place.
 *
 * With `editable` it also answers "map it, then". A course that OpenStreetMap never covered
 * and that nobody has walked with tracing on has no geometry at all and no way to acquire
 * any — the imagery analyser needs marked tees and greens before it can find bunkers or a
 * green boundary, so the course stays blank forever. Clicking the positions in breaks that,
 * which is why the editor exists here rather than as a separate screen: this is where the
 * gap is noticed.
 *
 * All the course's features are fetched once and paged through client-side — a course is at
 * most a few dozen rows, and refetching per hole would make the arrows feel broken.
 */
export function CourseHoleMapModal({
  visible,
  onClose,
  apiBaseUrl,
  token,
  courseId,
  holeNumbers = [],
  center = null,
  editable = false,
  initialHole = null,
  onHoleChange,
}: CourseHoleMapModalProps) {
  const { t } = useI18n();
  const distanceUnit = useDistanceUnit();
  const { height: windowHeight } = useWindowDimensions();
  const { features, shapes, loading, refresh, review } = useHoleFeatures(
    apiBaseUrl,
    token,
    visible ? courseId : null
  );
  const { save, saving } = useManualHoleFeatures(apiBaseUrl, token, courseId);
  // Only for the Lantmateriet basemap option — the picture, not the diagnostic panel's own
  // fetch (CourseAnalysisStatusPanel), which is a separate component with its own instance
  // of this hook. Held off the same way that one is: no point spending the request before
  // the map that would show it is even open.
  const { status: analysisStatus } = useCourseAnalysisStatus(apiBaseUrl, token, courseId, visible);
  const [index, setIndex] = useState(0);
  // The first page is a whole-course overview rather than hole 1, unless the caller asked to
  // open on a specific hole (`initialHole`) — then there is a place to be and no reason to
  // detour through the overview. Paging back from hole 1 returns here.
  const [overview, setOverview] = useState(initialHole == null);
  const [editing, setEditing] = useState(false);
  const [arming, setArming] = useState<'tee' | 'green' | null>(null);
  const [draft, setDraft] = useState<HoleDraft | null>(null);
  const [view, setView] = useState<ViewTransform>(IDENTITY_VIEW);
  const [message, setMessage] = useState<string | null>(null);
  /** A coordinate typed into the go-to field, once it has been read successfully. */
  const [manualCenter, setManualCenter] = useState<LatLon | null>(null);
  const [gotoText, setGotoText] = useState('');
  const [gotoInvalid, setGotoInvalid] = useState(false);
  /**
   * Boundary kinds switched off, which basemap picture is shown, and which shape sources are
   * drawn.
   *
   * All three persist across holes on purpose, unlike the frame: someone checking every
   * bunker on a course is asking the same question eighteen times, and having to switch the
   * other layers off again on each hole is how a review gets abandoned halfway round.
   */
  const [hiddenKinds, setHiddenKinds] = useState<string[]>([]);
  const [basemap, setBasemap] = useState<Basemap>('esri-worldimagery');
  const [sourceFilter, setSourceFilter] = useState<SourceOption[]>(SOURCE_OPTIONS);
  /** The shape a tap last landed on, shown as a callout instead of making the admin scroll
   * the list below to find it among however many the hole has. Looked up by id each render
   * rather than held as a snapshot, so it drops out on its own if the shape is filtered away. */
  const [selectedShapeId, setSelectedShapeId] = useState<string | null>(null);

  /**
   * The holes that can be paged to.
   *
   * The course's own holes plus any hole that has geometry — the second half catches a
   * feature attributed to a hole the scorecard does not have, which is worth seeing rather
   * than hiding. Falls back to mapped holes alone when the caller passes no scorecard, which
   * is the read-only behaviour this screen had before.
   */
  const holes = useMemo(
    () =>
      [...new Set([...holeNumbers, ...features.map((feature) => feature.holeNumber)])]
        .filter((number) => number > 0)
        .sort((a, b) => a - b),
    [features, holeNumbers]
  );

  // Clamped rather than reset: reloading after an import should keep the admin on the hole
  // they were inspecting, unless it no longer exists.
  const safeIndex = holes.length === 0 ? 0 : Math.min(index, holes.length - 1);
  const holeNumber = holes[safeIndex] ?? null;

  /**
   * Jumps to `initialHole` once, the first time it is found in `holes`.
   *
   * Retried on every `holes` recompute rather than just on `visible`, because `holes` starts
   * out as whatever `holeNumbers` alone contains and only gains the fetched holes afterwards
   * — a hole reachable solely through a feature attributed to it would otherwise miss the one
   * moment this looks. The ref stops it from firing again once it has, which is what lets the
   * admin page away from `initialHole` afterwards without being pulled back to it.
   */
  const seededInitialHoleRef = useRef(false);
  useEffect(() => {
    if (!visible) {
      seededInitialHoleRef.current = false;
      // Reset for the next open: the overview leads again unless the caller names a hole.
      setOverview(initialHole == null);
      return;
    }
    if (seededInitialHoleRef.current || initialHole == null) return;
    const target = holes.indexOf(initialHole);
    if (target < 0) return;
    seededInitialHoleRef.current = true;
    setIndex(target);
    setOverview(false);
  }, [visible, initialHole, holes]);

  // Reported only while open: a stale `holeNumber` lingering in state right after close is not
  // a navigation the caller's URL should end up reflecting.
  useEffect(() => {
    if (visible) onHoleChange?.(holeNumber);
  }, [visible, holeNumber, onHoleChange]);

  const holeFeatures = useMemo(
    () => features.filter((feature) => feature.holeNumber === holeNumber),
    [features, holeNumber]
  );

  /** The known tee or green closest to a hole number, whichever the course actually has. */
  const positionOfHole = useCallback(
    (number: number | undefined): LatLon | null => {
      if (number === undefined) return null;
      const holeFeaturesFor = features.filter((feature) => feature.holeNumber === number);
      const green = holeFeaturesFor.find((feature) => feature.kind === 'green');
      if (green) return { lat: green.lat, lon: green.lon };
      const tee = holeFeaturesFor.find((feature) => feature.kind === 'tee');
      return tee ? { lat: tee.lat, lon: tee.lon } : null;
    },
    [features]
  );

  /**
   * The best guess at where an unmapped hole actually is.
   *
   * Holes are walked in playing order, so the green just finished is usually a short walk
   * from the next tee — a far better first guess than the club's own coordinate, which can
   * sit anywhere on the property and be hundreds of metres from the hole being mapped. Falls
   * back to the following hole's tee when the previous one has nothing marked either.
   */
  const predictedCenter = useMemo(
    () => positionOfHole(holes[safeIndex - 1]) ?? positionOfHole(holes[safeIndex + 1]),
    [holes, positionOfHole, safeIndex]
  );

  /**
   * Every other hole that already has both a tee and a green marked, tee to green — so paging
   * blind through eighteen holes is not the only way to see which are already mapped.
   */
  const allHoleMarkers = useMemo(() => {
    const byHole = new Map<
      number,
      { tee: LatLon | null; teeOrdinal: number; green: LatLon | null }
    >();
    for (const feature of features) {
      const entry = byHole.get(feature.holeNumber) ?? {
        tee: null,
        teeOrdinal: Infinity,
        green: null,
      };
      if (feature.kind === 'tee' && feature.ordinal < entry.teeOrdinal) {
        entry.tee = { lat: feature.lat, lon: feature.lon };
        entry.teeOrdinal = feature.ordinal;
      }
      if (feature.kind === 'green') {
        entry.green = { lat: feature.lat, lon: feature.lon };
      }
      byHole.set(feature.holeNumber, entry);
    }
    const markers: KnownHoleMarker[] = [];
    for (const [number, entry] of byHole) {
      if (entry.tee && entry.green) {
        markers.push({ holeNumber: number, tee: entry.tee, green: entry.green });
      }
    }
    return markers.sort((a, b) => a.holeNumber - b.holeNumber);
  }, [features]);

  /** Every mapped hole but the one being viewed — drawn faintly under the active hole. */
  const otherHoles = useMemo(
    () => allHoleMarkers.filter((marker) => marker.holeNumber !== holeNumber),
    [allHoleMarkers, holeNumber]
  );

  // The draft follows the hole, and is rebuilt from the server after every save so what is
  // drawn is always what is stored rather than what was clicked.
  useEffect(() => {
    if (holeNumber === null) {
      setDraft(null);
      return;
    }
    setDraft(draftFromFeatures(holeNumber, features));
  }, [features, holeNumber]);

  useEffect(() => {
    // A new hole is a new frame; carrying the previous hole's pan over would open it
    // looking at the wrong piece of ground.
    setView(IDENTITY_VIEW);
    // A typed centre belongs to the hole it was typed on; carrying it to the next one would
    // silently frame that hole somewhere it has nothing to do with.
    setManualCenter(null);
    setGotoText('');
    setGotoInvalid(false);
    setArming(null);
  }, [holeNumber]);

  const dirty = useMemo(
    () => (draft ? isDraftDirty(draft, features) : false),
    [draft, features]
  );

  /** Tees and green as the canvas wants them, from a set of draft markers. */
  const teesAndGreenOf = (markers: HoleDraft['markers']) => {
    const tees: TeePoint[] = markers
      .filter((marker) => marker.kind === 'tee')
      .map((marker) => ({ lat: marker.lat, lon: marker.lon, label: marker.name }));
    const greenMarker = markers.find((marker) => marker.kind === 'green');
    return { tees, green: greenMarker ? { lat: greenMarker.lat, lon: greenMarker.lon } : null };
  };

  /** What the map draws: the draft while editing, the stored geometry otherwise. */
  const shown = useMemo(
    () =>
      teesAndGreenOf(
        editing && draft ? draft.markers : draftFromFeatures(holeNumber ?? 0, features).markers
      ),
    [draft, editing, features, holeNumber]
  );

  /**
   * The saved geometry, regardless of what is being edited right now.
   *
   * Used only for framing (see `frame` below) — never for what is drawn, which is `shown`.
   */
  const savedShown = useMemo(
    () => teesAndGreenOf(draftFromFeatures(holeNumber ?? 0, features).markers),
    [features, holeNumber]
  );

  /**
   * A tee and a green are both on record — the hole is a confirmed picture, not a guess.
   *
   * Used to tighten the frame (see `frame`'s pad fractions below): an unmapped hole wants the
   * wide, generous view that doubles as a course overview, but once a hole is confirmed there
   * is no reason to keep sharing the frame with whatever is next door.
   */
  const holeIsMapped = savedShown.tees.length > 0 && savedShown.green !== null;

  /** Every mapped boundary that belongs to this hole's picture, in draw order. */
  const holeShapes = useMemo(
    () => shapesForHole(shapes, holeNumber, shown.tees, shown.green),
    [holeNumber, shapes, shown]
  );

  /**
   * The same picture, but built from the saved geometry rather than the live draft, and
   * clipped to the hole's own corridor (see `framingRingsForHole`).
   *
   * An unmapped hole falls back to every shape on the course (see `shapesForHole`), and the
   * instant a tee is placed that set collapses down to just the nearby ones. Feeding that
   * straight into the map's frame would snap the view in around the click the moment it
   * happens — this keeps the frame on what was on screen when editing started instead.
   */
  const frameShapes = useMemo(
    () => framingRingsForHole(shapes, holeNumber, savedShown.tees, savedShown.green),
    [holeNumber, shapes, savedShown]
  );

  /** What the map's frame is fit to — see {@link HoleMapCanvas}'s `frame` prop. */
  const frame = useMemo(
    () => ({ tees: savedShown.tees, green: savedShown.green, polygons: frameShapes }),
    [frameShapes, savedShown]
  );

  /**
   * The overview page's frame: every mapped hole at once, so the fit zooms out to the whole
   * course. Greens ride in as single-point rings — the canvas fits `tees` plus every polygon
   * vertex — which is the cheapest way to fold both ends of every hole into one bound.
   */
  const overviewFrame = useMemo(
    () => ({
      tees: allHoleMarkers.map((marker) => ({ ...marker.tee, label: null })),
      green: null,
      polygons: allHoleMarkers.map((marker) => ({ ring: [marker.green] })),
    }),
    [allHoleMarkers]
  );

  /**
   * `holeShapes` narrowed to the chosen sources — independent of the basemap picture, which
   * only decides what is drawn underneath.
   *
   * A shape not attributable to any of the three sources on offer — manually placed, or a
   * bare GPS fix — is nobody's to claim, so it stays regardless of what is picked.
   */
  const providerShapes = useMemo(
    () =>
      holeShapes.filter((shape) => {
        if (shape.source === 'satellite') {
          return shape.sourceProvider
            ? sourceFilter.includes(shape.sourceProvider as SourceOption)
            : true;
        }
        if (shape.source === 'osm') return sourceFilter.includes('osm');
        return true;
      }),
    [sourceFilter, holeShapes]
  );

  /** The switches offered: one per kind actually found on this hole, with its tally. */
  const layers = useMemo(() => layersOf(providerShapes), [providerShapes]);

  const shownShapes = useMemo(
    () => visibleShapes(providerShapes, hiddenKinds),
    [hiddenKinds, providerShapes]
  );

  /**
   * The rings handed to the map.
   *
   * Over imagery they are outlines with a wash inside, so the ground stays readable through
   * them. Without imagery there is nothing to read through, and the flat cartographic fills —
   * the same ones the in-round yardage-book drawing uses — are what make the shapes tell
   * apart at a glance.
   */
  const showImagery = basemap === 'esri-worldimagery';

  /** The stored picture, only while that basemap is actually the one picked — switching
   * away and back should not keep an old image drawn under the wrong choice. */
  const storedBasemap = basemap === 'lantmateriet-orto' ? analysisStatus?.basemapImage ?? null : null;

  const polygons = useMemo<MapOverlayPolygon[]>(
    () =>
      shownShapes.map((shape) => {
        const outline = OVERLAY_COLORS[shape.kind] ?? '#FFFFFF';
        const flat = DRAWING_FILLS[shape.kind];
        return showImagery || !flat
          ? { id: shape.id, ring: shape.ring, color: outline }
          : { id: shape.id, ring: shape.ring, color: flat.stroke, fill: flat.fill, fillOpacity: 1 };
      }),
    [showImagery, shownShapes]
  );

  /** "Bunker", "Water" — falling back to the stored kind for anything not translated yet. */
  const kindLabel = (kind: string) => {
    const key = `courseHoleMap.shapeKind_${kind}`;
    const label = t(key);
    return label === key ? kind : label;
  };

  const describeShape = (shape: HoleShapeFeature) =>
    t('courseHoleMap.shapeMeta', {
      area: formatArea(ringAreaSquareMeters(shape.ring), distanceUnit),
      points: shape.ring.length,
      hole: shape.holeNumber,
    });

  // Looked up by id rather than kept as its own state, so a shape hidden via the layer bar
  // (or dropped by a refresh) takes the callout with it instead of leaving it pointing at
  // something no longer on the map.
  const selectedShape = shownShapes.find((shape) => shape.id === selectedShapeId) ?? null;

  const describe = (feature: HoleFeature) => {
    const kind = t(feature.kind === 'tee' ? 'courseMappingReview.tee' : 'courseMappingReview.green');
    if (feature.name) return `${kind} ${feature.name}`;
    return feature.kind === 'tee' && feature.ordinal > 0 ? `${kind} ${feature.ordinal + 1}` : kind;
  };

  const holeLength = useMemo(() => {
    if (!shown.green || shown.tees.length === 0) return null;
    // Longest tee, matching how the hole view measures it.
    const metres = Math.max(...shown.tees.map((tee) => haversineMetres(tee, shown.green!)));
    return formatHoleDistance(metres, distanceUnit);
  }, [distanceUnit, shown]);

  /**
   * The map's shape.
   *
   * Square is right on a phone, where the card is narrow and the map is all there is. In the
   * wide card it would be as tall as it is wide and push the tools, the coordinates and the
   * save button off the bottom of a laptop screen — so there it takes half the window height
   * and whatever width the card gives it.
   */
  const canvasStyle = useMemo(
    () =>
      editable
        ? // Never shrunk to fit what is below it — the card scrolls instead. Clicking a tee
          // box means aiming at a shape a few metres across, and a map squeezed to make room
          // for a coordinate listing is a map you cannot aim at.
          { flexShrink: 0, aspectRatio: undefined, height: Math.max(240, windowHeight * 0.45) }
        : { flexShrink: 0 },
    [editable, windowHeight]
  );

  const onMapPress = useCallback(
    (point: LatLon) => {
      if (editing && arming && draft) {
        setDraft(placeMarker(draft, arming, point));
        // One click, one marker: leaving it armed made a second look at the map place a
        // second tee nobody asked for.
        setArming(null);
        setMessage(null);
        return;
      }
      // Not placing a marker: a tap is asking what is drawn at that point, not editing it.
      setSelectedShapeId(shapeAt(shownShapes, point)?.id ?? null);
    },
    [arming, draft, editing, shownShapes]
  );

  /**
   * Writes a corrected course coordinate.
   *
   * Analysis is requested with it: whatever was analysed before was imagery of the wrong
   * place, so the stored features for this course are answers to a different question.
   */
  const saveCoursePosition = useCallback(async (point: LatLon): Promise<boolean> => {
    if (!courseId || !token) return false;
    try {
      const response = await fetch(`${apiBaseUrl}/api/geodata/courses/${courseId}/position`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ lat: point.lat, lon: point.lon, requestAnalysis: true }),
      });
      return response.ok;
    } catch {
      return false;
    }
  }, [apiBaseUrl, courseId, token]);

  const onSave = useCallback(async () => {
    if (!draft) return;
    setMessage(null);

    // Deletions first. A marker removed here has a server row that a save would not touch —
    // the write only upserts what it is given — so leaving them for afterwards would mean a
    // failure halfway through resurrects them on the next refresh.
    const removed = removedFeatureIds(draft, features);
    for (const featureId of removed) {
      const deleted = await review(featureId, false);
      if (!deleted) {
        setMessage(t('courseHoleMap.saveFailed'));
        return;
      }
    }

    const payload = toManualFeatures(draft);
    if (payload.length > 0) {
      const saved = await save(payload);
      if (!saved) {
        setMessage(t('courseHoleMap.saveFailed'));
        return;
      }
    }

    // The course's own coordinate frames every hole that has nothing marked yet, and it is
    // where the imagery analyser points its tiles. Marking a hole kilometres away from it is
    // evidence that the coordinate is the thing that is wrong — so it is moved to match, and
    // with it every other hole's starting frame. Re-analysis is requested because whatever
    // was analysed before was a picture of the wrong place.
    const corrected = correctedCoursePosition(
      center,
      draft.markers.map((marker) => ({ lat: marker.lat, lon: marker.lon }))
    );
    if (corrected) {
      const moved = await saveCoursePosition(corrected);
      // The geometry is already saved; a failure here costs the reframing, not the work.
      setMessage(t(moved ? 'courseHoleMap.savedAndMoved' : 'courseHoleMap.saved'));
      refresh();
      return;
    }

    refresh();
    setMessage(t('courseHoleMap.saved'));
  }, [center, draft, features, refresh, review, save, saveCoursePosition, t]);

  const [analysing, setAnalysing] = useState(false);

  /** Queues imagery analysis for the course, from the screen where the geometry was marked. */
  const onAnalyse = async () => {
    if (analysing) return;
    setAnalysing(true);
    const outcome = await requestCourseAnalysis(apiBaseUrl, token, courseId);
    setAnalysing(false);
    setMessage(
      t(
        outcome === 'queued'
          ? 'courseHoleMap.analysisQueued'
          : outcome === 'unavailable'
            ? 'courseHoleMap.analysisUnavailable'
            : 'courseHoleMap.analysisFailed'
      )
    );
  };

  const stepZoom = (factor: number) =>
    setView((current) => ({
      ...current,
      // The editor may go below the fit: the frame is built from the geometry being
      // corrected, so when that geometry is wrong the ground you need is outside it.
      zoom: Math.min(MAX_VIEW_ZOOM, Math.max(editing ? MIN_EDIT_ZOOM : MIN_VIEW_ZOOM, current.zoom * factor)),
    }));

  /**
   * Moves the frame to a typed coordinate.
   *
   * The pan and zoom are reset with it, or the new centre would be pushed straight back off
   * screen by however far the old view had been dragged — which looks exactly like the field
   * not working.
   */
  const goToTypedCoordinate = () => {
    const point = parseLatLon(gotoText);
    if (!point) {
      setGotoInvalid(true);
      return;
    }
    setGotoInvalid(false);
    setManualCenter(point);
    setView(IDENTITY_VIEW);
  };

  const goToHole = (nextIndex: number) => {
    if (dirty && editing) {
      // Nothing is discarded silently: the warning is the whole protection, since the draft
      // only lives in this component.
      setMessage(t('courseHoleMap.unsavedWarning'));
      return;
    }
    setIndex(nextIndex);
    setMessage(null);
    setSelectedShapeId(null);
  };

  /**
   * Leaves edit mode, unless that would drop work.
   *
   * The draft lives only in this component, so the warning is the whole protection — and
   * Undo is right beside the button that refuses, which is what makes refusing reasonable.
   */
  const stopEditing = () => {
    if (dirty) {
      setMessage(t('courseHoleMap.unsavedWarning'));
      return;
    }
    setEditing(false);
    setArming(null);
    setMessage(null);
    setSelectedShapeId(null);
  };

  /**
   * Closing with unsaved work warns first and closes on the second press.
   *
   * A modal that will not close is worse than one that loses a couple of clicks, so this
   * asks rather than refuses — unlike moving between holes, where staying put costs nothing.
   */
  const closeModal = () => {
    if (editing && dirty && message !== t('courseHoleMap.closeWarning')) {
      setMessage(t('courseHoleMap.closeWarning'));
      return;
    }
    setEditing(false);
    setArming(null);
    setMessage(null);
    setSelectedShapeId(null);
    onClose();
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={closeModal}>
      <View style={styles.backdrop}>
        <View style={[styles.card, editable ? styles.cardWide : null]}>
          <View style={styles.header}>
            <Pressable
              testID="course-hole-map-prev"
              accessibilityRole="button"
              // The overview is the first page — there is nothing before it. From hole 1, Prev
              // steps back out to the overview rather than being dead.
              disabled={overview}
              style={[styles.navButton, overview && styles.navButtonDisabled]}
              onPress={() => (safeIndex <= 0 ? setOverview(true) : goToHole(safeIndex - 1))}
            >
              <Text style={styles.navText}>‹</Text>
            </Pressable>

            <View style={styles.headerText}>
              <Text style={styles.title} testID="course-hole-map-title">
                {overview
                  ? t('courseHoleMap.overviewTitle')
                  : holeNumber === null
                    ? t('courseHoleMap.title')
                    : t('courseHoleMap.holeTitle', { hole: holeNumber })}
              </Text>
              {!overview && holeLength ? <Text style={styles.subtitle}>{holeLength}</Text> : null}
            </View>

            <Pressable
              testID="course-hole-map-next"
              accessibilityRole="button"
              // From the overview, Next enters the holes at whichever one the pager last sat on
              // (hole 1 on a fresh open). Within the holes it advances until the last one.
              disabled={overview ? holes.length === 0 : safeIndex >= holes.length - 1}
              style={[
                styles.navButton,
                (overview ? holes.length === 0 : safeIndex >= holes.length - 1) &&
                  styles.navButtonDisabled,
              ]}
              onPress={() => (overview ? setOverview(false) : goToHole(safeIndex + 1))}
            >
              <Text style={styles.navText}>›</Text>
            </Pressable>
          </View>

          {loading ? (
            <View style={styles.empty} testID="course-hole-map-loading">
              <ActivityIndicator color={appColors.accent.primary} />
            </View>
          ) : overview && holes.length > 0 ? (
            /* The complete-course overview: every hole drawn at once, each a numbered marker
               that jumps into its own page. No per-hole tools here — it is a way in, not a
               place to edit. */
            <View style={styles.body}>
              <HoleMapCanvas
                tees={NO_TEES}
                green={null}
                loading={false}
                // The overview never draws a single active hole, so there is no "this hole has
                // no green" curtain to raise — the numbered markers are the whole content.
                unavailable={false}
                showImagery={showImagery}
                storedBasemap={storedBasemap}
                fallbackCenter={center}
                fallbackSpanMeters={FALLBACK_SPAN_METERS}
                frame={overviewFrame}
                canvasStyle={canvasStyle}
                view={view}
                onPan={(pan) => setView((current) => ({ ...current, ...pan }))}
                minZoom={MIN_VIEW_ZOOM}
                otherHoles={allHoleMarkers}
                onSelectHole={(number) => {
                  const target = holes.indexOf(number);
                  if (target >= 0) {
                    setOverview(false);
                    goToHole(target);
                  }
                }}
              />
              <Text style={styles.counter} testID="course-hole-map-overview-counter">
                {t('courseHoleMap.overviewCounter', { total: holes.length })}
              </Text>
            </View>
          ) : holeNumber === null ? (
            <View style={styles.empty} testID="course-hole-map-empty">
              <Text style={styles.emptyText}>{t('courseHoleMap.empty')}</Text>
            </View>
          ) : (
            /* The card scrolls as one, rather than the reading being a scrolling box inside a
               fixed one. With both the map and the list wanting a height, a card capped at
               90% of the window has to take it from somewhere, and every way of dividing it
               starves one of them on some screen — the list collapsed to a single line on a
               laptop, and giving way instead left the map too small to click accurately. */
            <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
              <HoleMapCanvas
                tees={shown.tees}
                green={shown.green}
                loading={false}
                polygons={polygons}
                showImagery={showImagery}
                storedBasemap={storedBasemap}
                // In edit mode an empty hole is the job, not a failure, so the "no geometry"
                // curtain would only be in the way of the map being clicked. Nor is it a
                // failure when the hole has boundaries but no green: there is a picture to
                // look at, and covering it says the opposite.
                unavailable={!editing && shown.green === null && holeShapes.length === 0}
                // The previous (or next) hole's own position beats the club's coordinate as a
                // starting guess — see `predictedCenter` — and only when neither is known does
                // this fall back to the club's, over a wider span since that could be anywhere
                // on the property.
                fallbackCenter={manualCenter ?? predictedCenter ?? center}
                fallbackSpanMeters={
                  manualCenter || !predictedCenter ? FALLBACK_SPAN_METERS : PREDICTED_SPAN_METERS
                }
                // While editing the view stays put as markers go down, so placing a green
                // 300 m from the tee does not mean hunting for it after every click.
                holdFallbackFrame={editing}
                // The frame is fit to the saved geometry, not the live draft — see the prop's
                // own doc. Without this, placing a tee or green recentres the map on the spot
                // just clicked, which reads as the map moving out from under the click.
                frame={frame}
                // A confirmed hole fills the frame instead of sharing it with the neighbours —
                // an unmapped hole keeps the wide defaults, which is what makes it double as a
                // course overview (see `holeIsMapped`'s own doc).
                horizontalPadFraction={holeIsMapped ? MAPPED_HORIZONTAL_PAD : undefined}
                verticalPadFraction={holeIsMapped ? MAPPED_VERTICAL_PAD : undefined}
                view={view}
                canvasStyle={canvasStyle}
                aiming={arming !== null}
                // Always wired, not just while editing: `onMapPress` itself branches on
                // `editing`/`arming` to decide between placing a marker and inspecting
                // whatever shape the tap landed on — the read-only map wants the second one.
                onMapPress={onMapPress}
                // Panning is not an editing tool, and gating it on edit mode made the
                // read-only map a picture you could zoom into and then not move around in:
                // the +/- buttons drive the view directly and kept working, so the map
                // responded to every control except dragging it. A drag under the threshold
                // still resolves as a tap, and the hole markers are Pressables, so tapping a
                // neighbouring hole to jump to it is unaffected.
                onPan={(pan) => setView((current) => ({ ...current, ...pan }))}
                // Only while editing: it is a tool for checking that the frame is over the
                // right ground, which is a question the person moving markers is asking.
                showCenterCoordinate={editing}
                minZoom={editing ? MIN_EDIT_ZOOM : MIN_VIEW_ZOOM}
                otherHoles={otherHoles}
                // Left out while editing: a tap near a marker should never compete with placing
                // or panning.
                onSelectHole={
                  editing
                    ? undefined
                    : (number) => {
                        const target = holes.indexOf(number);
                        if (target >= 0) goToHole(target);
                      }
                }
              />

              {/* What a tap on the map landed on — the same row content the list below
                  already gives every shape, surfaced here so finding out which of however
                  many bunkers this hole has does not mean scrolling to hunt for it. */}
              {selectedShape ? (
                <View style={styles.shapeInfoCard} testID="course-hole-map-shape-info">
                  <View style={styles.shapeInfoHeading}>
                    <View style={styles.shapeHeading}>
                      <View
                        style={[
                          styles.layerSwatch,
                          { backgroundColor: OVERLAY_COLORS[selectedShape.kind] ?? '#FFFFFF' },
                        ]}
                      />
                      <Text style={styles.rowLabel}>{kindLabel(selectedShape.kind)}</Text>
                    </View>
                    <Pressable
                      testID="course-hole-map-shape-info-close"
                      accessibilityRole="button"
                      onPress={() => setSelectedShapeId(null)}
                    >
                      <Text style={styles.toolText}>{t('courseHoleMap.close')}</Text>
                    </Pressable>
                  </View>
                  <Text style={styles.rowCoords}>{describeShape(selectedShape)}</Text>
                  <Text style={styles.rowMeta}>
                    {t('courseHoleMap.meta', {
                      source: sourceLabel(selectedShape.source, selectedShape.sourceProvider),
                      confidence: Math.round(selectedShape.confidence * 100),
                      contributors: selectedShape.sampleCount,
                    })}
                  </Text>
                </View>
              ) : null}

              {/* Offered in edit mode too. Marking a green is aiming at a shape the analyser
                  has often already outlined, and being able to see that outline — or to take
                  the imagery away and check the outline is not nonsense — is the difference
                  between placing a point and guessing at one. */}
              {layers.length > 0 ? (
                <View style={styles.layerBar} testID="course-hole-map-layers">
                  {layers.map((layer) => {
                    const hidden = hiddenKinds.includes(layer.kind);
                    return (
                      <Pressable
                        key={layer.kind}
                        testID={`course-hole-map-layer-${layer.kind}`}
                        accessibilityRole="button"
                        aria-pressed={!hidden}
                        style={[styles.layerChip, hidden ? styles.layerChipOff : null]}
                        onPress={() => setHiddenKinds((current) => toggleKind(current, layer.kind))}
                      >
                        {/* The swatch is the legend: it is the only thing tying the row to
                            the colour on the map, and it survives the label being long. */}
                        <View
                          style={[
                            styles.layerSwatch,
                            { backgroundColor: OVERLAY_COLORS[layer.kind] ?? '#FFFFFF' },
                            hidden ? styles.layerSwatchOff : null,
                          ]}
                        />
                        <Text style={styles.layerText}>
                          {kindLabel(layer.kind)} {layer.count}
                        </Text>
                      </Pressable>
                    );
                  })}

                  <View style={[styles.basemapGroup, styles.imageryChip]}>
                    {BASEMAPS.map((option) => {
                      const active = basemap === option;
                      return (
                        <Pressable
                          key={option}
                          testID={`course-hole-map-basemap-${option}`}
                          accessibilityRole="button"
                          aria-pressed={active}
                          style={[styles.layerChip, active ? null : styles.layerChipOff]}
                          onPress={() => setBasemap(option)}
                        >
                          <Text style={styles.layerText}>{t(`courseHoleMap.basemap_${option}`)}</Text>
                        </Pressable>
                      );
                    })}
                  </View>
                </View>
              ) : null}

              {/* Independent of the basemap picture above: this decides which shape sources
                  are drawn, and — unlike the basemap — more than one can be on at once, so a
                  provider's satellite detections and OSM's own import can be compared side
                  by side. Gated on the hole's raw shapes, not the filtered `layers`, so
                  switching every source off does not also hide the way to switch one back
                  on. */}
              {holeShapes.length > 0 ? (
                <View style={styles.layerBar} testID="course-hole-map-sources">
                  {SOURCE_OPTIONS.map((option) => {
                    const active = sourceFilter.includes(option);
                    return (
                      <Pressable
                        key={option}
                        testID={`course-hole-map-source-${option}`}
                        accessibilityRole="button"
                        aria-pressed={active}
                        style={[styles.layerChip, active ? null : styles.layerChipOff]}
                        onPress={() =>
                          setSourceFilter((current) =>
                            current.includes(option)
                              ? current.filter((selected) => selected !== option)
                              : [...current, option]
                          )
                        }
                      >
                        <Text style={styles.layerText}>{t(`courseHoleMap.source_${option}`)}</Text>
                      </Pressable>
                    );
                  })}
                </View>
              ) : null}

              {basemap === 'lantmateriet-orto' && !storedBasemap ? (
                <Text style={styles.hint} testID="course-hole-map-basemap-unavailable">
                  {t('courseHoleMap.basemapUnavailable')}
                </Text>
              ) : null}

              {editable ? (
                <View style={styles.editBar}>
                  <Pressable
                    testID="course-hole-map-edit-toggle"
                    accessibilityRole="button"
                    style={[styles.toolButton, editing ? styles.toolButtonActive : null]}
                    onPress={() => {
                      if (editing) {
                        stopEditing();
                        return;
                      }
                      setEditing(true);
                      setArming(null);
                      setMessage(null);
                    }}
                  >
                    <Text style={styles.toolText}>
                      {t(editing ? 'courseHoleMap.editDone' : 'courseHoleMap.edit')}
                    </Text>
                  </Pressable>

                  {editing ? (
                    <>
                      <Pressable
                        testID="course-hole-map-place-tee"
                        accessibilityRole="button"
                        style={[styles.toolButton, arming === 'tee' ? styles.toolButtonActive : null]}
                        onPress={() => setArming(arming === 'tee' ? null : 'tee')}
                      >
                        <Text style={styles.toolText}>{t('courseHoleMap.placeTee')}</Text>
                      </Pressable>
                      <Pressable
                        testID="course-hole-map-place-green"
                        accessibilityRole="button"
                        style={[
                          styles.toolButton,
                          arming === 'green' ? styles.toolButtonActive : null,
                        ]}
                        onPress={() => setArming(arming === 'green' ? null : 'green')}
                      >
                        <Text style={styles.toolText}>{t('courseHoleMap.placeGreen')}</Text>
                      </Pressable>
                      <Pressable
                        testID="course-hole-map-revert"
                        accessibilityRole="button"
                        disabled={!dirty || saving}
                        style={[styles.toolButton, !dirty || saving ? styles.toolButtonDisabled : null]}
                        onPress={() => {
                          setDraft(draftFromFeatures(holeNumber, features));
                          setArming(null);
                          setMessage(null);
                        }}
                      >
                        <Text style={styles.toolText}>{t('courseHoleMap.revert')}</Text>
                      </Pressable>
                      <Pressable
                        testID="course-hole-map-save"
                        accessibilityRole="button"
                        disabled={!dirty || saving}
                        style={[
                          styles.toolButton,
                          styles.saveButton,
                          !dirty || saving ? styles.toolButtonDisabled : null,
                        ]}
                        onPress={() => void onSave()}
                      >
                        <Text style={styles.toolText}>
                          {saving ? t('courseHoleMap.saving') : t('courseHoleMap.save')}
                        </Text>
                      </Pressable>
                    </>
                  ) : null}

                  {/* Outside the `editing` block on purpose: re-running the analyser is not
                      only useful right after marking a tee and green — a hole that is already
                      mapped may need a rerun too (fresher imagery, a previous run that came
                      back thin), and that should not require opening the editor first. Still
                      disabled on `dirty`, which stays false outside editing anyway, since the
                      analyser reads whatever is saved, not whatever is still in the draft. */}
                  <Pressable
                    testID="course-hole-map-analyse"
                    accessibilityRole="button"
                    disabled={analysing || dirty}
                    style={[styles.toolButton, analysing || dirty ? styles.toolButtonDisabled : null]}
                    onPress={() => void onAnalyse()}
                  >
                    <Text style={styles.toolText}>
                      {analysing ? t('courseHoleMap.analysing') : t('courseHoleMap.analyse')}
                    </Text>
                  </Pressable>

                  <View style={styles.zoomGroup}>
                    <Pressable
                      testID="course-hole-map-zoom-in"
                      accessibilityRole="button"
                      disabled={view.zoom >= MAX_VIEW_ZOOM}
                      style={styles.zoomButton}
                      onPress={() => stepZoom(ZOOM_STEP)}
                    >
                      <Text style={styles.toolText}>+</Text>
                    </Pressable>
                    <Pressable
                      testID="course-hole-map-zoom-out"
                      accessibilityRole="button"
                      disabled={view.zoom <= (editing ? MIN_EDIT_ZOOM : MIN_VIEW_ZOOM)}
                      style={styles.zoomButton}
                      onPress={() => stepZoom(1 / ZOOM_STEP)}
                    >
                      <Text style={styles.toolText}>−</Text>
                    </Pressable>
                    <Pressable
                      testID="course-hole-map-zoom-fit"
                      accessibilityRole="button"
                      style={styles.zoomButton}
                      onPress={() => setView(IDENTITY_VIEW)}
                    >
                      <Text style={styles.toolText}>⤢</Text>
                    </Pressable>
                  </View>
                </View>
              ) : null}

              {/* Typing a coordinate is the way back when the frame is over the wrong ground
                  entirely. Panning only helps once you are near; a course whose stored
                  position is wrong can be kilometres out, and the imagery gives nothing to
                  navigate by. Paste the club's real coordinate and the map goes there. */}
              {editing ? (
                <View style={styles.gotoRow}>
                  <TextInput
                    testID="course-hole-map-goto-input"
                    style={styles.gotoInput}
                    value={gotoText}
                    onChangeText={(text) => {
                      setGotoText(text);
                      setGotoInvalid(false);
                    }}
                    onSubmitEditing={goToTypedCoordinate}
                    placeholder={t('courseHoleMap.gotoPlaceholder')}
                    placeholderTextColor={appColors.text.muted}
                    autoCapitalize="none"
                    autoCorrect={false}
                    inputMode="text"
                    returnKeyType="go"
                  />
                  <Pressable
                    testID="course-hole-map-goto"
                    accessibilityRole="button"
                    disabled={gotoText.trim().length === 0}
                    style={[
                      styles.toolButton,
                      gotoText.trim().length === 0 ? styles.toolButtonDisabled : null,
                    ]}
                    onPress={goToTypedCoordinate}
                  >
                    <Text style={styles.toolText}>{t('courseHoleMap.goto')}</Text>
                  </Pressable>
                </View>
              ) : null}

              {gotoInvalid ? (
                <Text style={styles.gotoError} testID="course-hole-map-goto-invalid">
                  {t('courseHoleMap.gotoInvalid')}
                </Text>
              ) : null}

              {editing ? (
                <Text style={styles.hint} testID="course-hole-map-hint">
                  {t(arming === null ? 'courseHoleMap.editHint' : 'courseHoleMap.armedHint')}
                </Text>
              ) : null}

              {message ? (
                <Text style={styles.message} testID="course-hole-map-message">
                  {message}
                </Text>
              ) : null}

              {/* A plain view, not a scroller: the card is the scroller now, and a scrolling
                  box inside a scrolling box is a trap for the wheel. */}
              <View>
                {editing && draft
                  ? draft.markers.map((marker) => (
                      <View
                        key={markerKey(marker)}
                        style={styles.editRow}
                        testID={`course-hole-map-draft-${markerKey(marker)}`}
                      >
                        <View style={styles.editRowText}>
                          <Text style={styles.rowLabel}>
                            {t(
                              marker.kind === 'tee'
                                ? 'courseMappingReview.tee'
                                : 'courseMappingReview.green'
                            )}
                            {marker.kind === 'tee' && marker.ordinal > 0
                              ? ` ${marker.ordinal + 1}`
                              : ''}
                            {marker.name ? ` ${marker.name}` : ''}
                          </Text>
                          <Text style={styles.rowCoords} selectable>
                            {marker.lat.toFixed(6)}, {marker.lon.toFixed(6)}
                          </Text>
                        </View>
                        <Pressable
                          testID={`course-hole-map-remove-${markerKey(marker)}`}
                          accessibilityRole="button"
                          style={styles.removeButton}
                          onPress={() => setDraft(removeMarker(draft, markerKey(marker)))}
                        >
                          <Text style={styles.toolText}>{t('courseHoleMap.remove')}</Text>
                        </Pressable>
                      </View>
                    ))
                  : holeFeatures.map((feature) => (
                      <View key={feature.id} style={styles.row} testID={`course-hole-map-row-${feature.id}`}>
                        <Text style={styles.rowLabel}>{describe(feature)}</Text>
                        {/* Six decimals is ~0.1m — finer than anything upstream, and the precision
                            an admin needs to paste a coordinate somewhere else and compare. */}
                        <Text style={styles.rowCoords} selectable>
                          {feature.lat.toFixed(6)}, {feature.lon.toFixed(6)}
                        </Text>
                        <Text style={styles.rowMeta} testID={`course-hole-map-row-meta-${feature.id}`}>
                          {t('courseHoleMap.meta', {
                            source: sourceLabel(feature.source, feature.sourceProvider),
                            confidence: Math.round(feature.confidence * 100),
                            contributors: feature.sampleCount,
                          })}
                        </Text>
                      </View>
                    ))}

                {/* Below the points in both modes, including while editing: a boundary is not
                    editable here, so it is context for the marker being placed rather than
                    another row to act on. Filtered with the map, so switching a kind off
                    takes it out of the reading as well as out of the picture. */}
                {shownShapes.map((shape) => (
                  <View
                    key={shape.id}
                    style={styles.row}
                    testID={`course-hole-map-shape-${shape.id}`}
                  >
                    <View style={styles.shapeHeading}>
                      <View
                        style={[
                          styles.layerSwatch,
                          { backgroundColor: OVERLAY_COLORS[shape.kind] ?? '#FFFFFF' },
                        ]}
                      />
                      <Text style={styles.rowLabel}>{kindLabel(shape.kind)}</Text>
                    </View>
                    <Text style={styles.rowCoords}>{describeShape(shape)}</Text>
                    <Text style={styles.rowMeta} testID={`course-hole-map-shape-meta-${shape.id}`}>
                      {t('courseHoleMap.meta', {
                        source: sourceLabel(shape.source, shape.sourceProvider),
                        confidence: Math.round(shape.confidence * 100),
                        contributors: shape.sampleCount,
                      })}
                    </Text>
                  </View>
                ))}
              </View>

              <Text style={styles.counter}>
                {t('courseHoleMap.counter', { index: safeIndex + 1, total: holes.length })}
              </Text>
            </ScrollView>
          )}

          <Pressable
            accessibilityRole="button"
            testID="course-hole-map-close"
            style={styles.closeButton}
            onPress={closeModal}
          >
            <Text style={styles.closeText}>{t('menu.close')}</Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}

const EARTH_RADIUS_METRES = 6_371_000;

/** Local copy so this modal does not depend on the round-scoped hole-view internals. */
const haversineMetres = (from: LatLon, to: LatLon): number => {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(to.lat - from.lat);
  const dLon = toRad(to.lon - from.lon);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(from.lat)) * Math.cos(toRad(to.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_METRES * Math.asin(Math.min(1, Math.sqrt(a)));
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  card: {
    width: '100%',
    maxWidth: 460,
    maxHeight: '90%',
    borderRadius: 12,
    padding: 14,
    gap: 10,
    backgroundColor: appColors.background.panel,
  },
  // Marking a tee box means clicking a shape a few metres across. At 460px that is a
  // handful of pixels; on a laptop there is no reason to make someone aim at it.
  cardWide: Platform.select({ web: { maxWidth: 900 }, default: {} }) as object,
  header: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  headerText: { flex: 1, alignItems: 'center' },
  title: { color: appColors.text.primary, fontSize: 18, fontWeight: '700' },
  subtitle: { color: 'rgba(226,232,240,0.7)', fontSize: 13, marginTop: 2 },
  navButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(226,232,240,0.35)',
  },
  navButtonDisabled: { opacity: 0.3 },
  navText: { color: appColors.text.primary, fontSize: 22, lineHeight: 26 },
  empty: { paddingVertical: 40, alignItems: 'center' },
  emptyText: { color: appColors.text.primary, fontSize: 14, textAlign: 'center' },
  editBar: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 8 },
  toolButton: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(226,232,240,0.35)',
  },
  toolButtonActive: { borderColor: '#A1D493', backgroundColor: 'rgba(161,212,147,0.18)' },
  toolButtonDisabled: { opacity: 0.4 },
  saveButton: { borderColor: '#A1D493' },
  zoomGroup: { flexDirection: 'row', gap: 6, marginLeft: 'auto' },
  gotoRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  gotoInput: {
    flex: 1,
    minWidth: 140,
    height: 36,
    paddingHorizontal: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.surface.base,
    color: appColors.text.primary,
    fontSize: 13,
    fontVariant: ['tabular-nums'],
  },
  gotoError: { color: appColors.status.warning, fontSize: 12, lineHeight: 16 },
  zoomButton: {
    width: 34,
    height: 34,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(226,232,240,0.35)',
  },
  toolText: { color: appColors.text.primary, fontSize: 14, fontWeight: '600' },
  hint: { color: 'rgba(226,232,240,0.7)', fontSize: 12 },
  message: { color: appColors.text.primary, fontSize: 13 },
  layerBar: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 6 },
  layerChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 4,
    paddingHorizontal: 9,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: 'rgba(226,232,240,0.35)',
  },
  // Switched off reads as faded rather than as a different control: the chip still has to say
  // what it would bring back.
  layerChipOff: { opacity: 0.4 },
  imageryChip: { marginLeft: 'auto' },
  basemapGroup: { flexDirection: 'row', gap: 6 },
  layerSwatch: { width: 10, height: 10, borderRadius: 2 },
  layerSwatchOff: { opacity: 0.5 },
  layerText: { color: appColors.text.primary, fontSize: 12, fontWeight: '600' },
  body: { flexShrink: 1 },
  // The gap the card used to provide for these children, now that they are one scroller's
  // content rather than the card's own children.
  bodyContent: { gap: 10 },
  row: { paddingVertical: 6 },
  shapeHeading: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  shapeInfoCard: {
    borderWidth: 1,
    borderColor: 'rgba(226,232,240,0.35)',
    borderRadius: 10,
    padding: 10,
    gap: 2,
  },
  shapeInfoHeading: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  editRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    paddingVertical: 6,
  },
  editRowText: { flexShrink: 1 },
  removeButton: {
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(226,232,240,0.35)',
  },
  rowLabel: { color: appColors.text.primary, fontSize: 14, fontWeight: '600' },
  rowCoords: { color: 'rgba(226,232,240,0.85)', fontSize: 13, marginTop: 1 },
  rowMeta: { color: 'rgba(226,232,240,0.55)', fontSize: 11, marginTop: 1 },
  counter: { color: 'rgba(226,232,240,0.6)', fontSize: 12, textAlign: 'center' },
  closeButton: {
    alignSelf: 'center',
    paddingVertical: 10,
    paddingHorizontal: 24,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#A1D493',
  },
  closeText: { color: appColors.text.primary, fontSize: 15, fontWeight: '700' },
});
